import { Stack, Button, Alert, Chip, CircularProgress, Link } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useCanReviewPartners } from 'src/hooks/usePartnerOps';
import { SectionModal } from './section-modals/SectionModal';
import { useCourseView } from './useCourseView';
import { SectionSummery } from './Section-Summery/SectionSummery';
import { SectionItemModal } from './section-modals/SectionItemModal';
import { useState } from 'react';
import { StyledTextMaxLine } from './styles';
import ConfirmationDialog from './section-item-table/ConformiationDialog';
import { TableNoData } from 'src/components/table';
import { repairsText } from './lessonOrder';

// ----------------------------------------------------------------------

export const DRAFT_TEXT = 'Draft — hidden from students';
export const PARTNER_NOTICE_TEXT =
  'This course belongs to a partner. Its lessons can be read here but are managed in Partner courses.';
export const PARTNER_ADMIN_ONLY_TEXT = 'Managed in Partner courses (admin only)';

// ----------------------------------------------------------------------

export default function CourseItem() {
  const {
    currentCourse,
    loading,
    loadError,
    retry,
    pageError,
    setPageError,
    sectionStatus,
    clearSectionError,
    saving,
    readOnly,
    repairNeeded,
    sectionModalOpen,
    editingSection,
    currentSection,
    openNewSection,
    openEditSection,
    closeSectionModal,
    onSubmitNewSection,
    onDeleteSection,
    modalOpen,
    modalStep,
    setModalStep,
    currentItem,
    openNewLesson,
    openEditLesson,
    closeLessonModal,
    onSubmitNewSectionItem,
    onDeleteItem,
    moveLesson,
    repairLessons,
  } = useCourseView();
  const canOpenPartnerPages = useCanReviewPartners();
  const [deleteSectionIndex, setDeleteSectionIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<string | false>(false);
  const navigate = useNavigate();

  if (loading && !currentCourse) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }} data-testid="course-loading">
        <CircularProgress />
      </Stack>
    );
  }

  if (!currentCourse) {
    return (
      <Stack spacing={2} sx={{ py: 4 }}>
        <Alert
          severity="error"
          data-testid="course-load-error"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Retry
            </Button>
          }
        >
          {`The course could not be loaded. ${loadError?.text ?? ''}`.trim()}
        </Alert>
        <Button
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => navigate(PATH_DASHBOARD.courses.root)}
          startIcon={<Iconify icon={'eva:arrow-back-fill'} />}
        >
          Course List
        </Button>
      </Stack>
    );
  }

  const courseId = currentCourse.id || currentCourse._id;
  const sections = currentCourse.sections ?? [];
  // Locked until "Fix lesson problems": every lesson and section action waits.
  const locked = repairNeeded;

  return (
    <Stack>
      <HeaderBreadcrumbs
        heading={currentCourse.name as string}
        links={[
          { name: 'Dashboard', href: PATH_DASHBOARD.root },
          { name: 'Course List', href: PATH_DASHBOARD.courses.root },

          {
            name: currentCourse.name as string,
            href: PATH_DASHBOARD.courses.view(courseId),
          },
        ]}
        action={
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              onClick={() => navigate(`${PATH_DASHBOARD.courses.view(courseId)}/report`)}
              startIcon={<Iconify icon={'eva:bar-chart-2-outline'} />}
            >
              Report
            </Button>
            {!readOnly && (
              <Button
                variant="contained"
                disabled={saving || locked}
                onClick={openNewSection}
                startIcon={<Iconify icon={'eva:plus-fill'} />}
              >
                New Section
              </Button>
            )}
          </Stack>
        }
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        {currentCourse.active === false && (
          <Chip label={DRAFT_TEXT} color="warning" size="small" data-testid="course-draft-chip" />
        )}
        {readOnly && <Chip label="Partner course" color="info" size="small" />}
      </Stack>

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="course-partner-notice">
          {PARTNER_NOTICE_TEXT}{' '}
          {canOpenPartnerPages ? (
            <Link component={RouterLink} to={PATH_DASHBOARD.partners.courseDetail(courseId)}>
              Open in Partner courses
            </Link>
          ) : (
            PARTNER_ADMIN_ONLY_TEXT
          )}
        </Alert>
      )}

      {locked && !readOnly && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="course-repairs-banner"
          action={
            <Button color="inherit" size="small" disabled={saving} onClick={repairLessons}>
              Fix lesson problems
            </Button>
          }
        >
          {`This course has lesson problems: ${repairsText(currentCourse.repairs)}. ` +
            'Lessons cannot be changed until they are fixed. Fixing removes empty slots and ' +
            'gives every lesson its own id; nothing else changes.'}
        </Alert>
      )}

      {pageError && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert" onClose={() => setPageError(null)}>
          {pageError}
        </Alert>
      )}

      <StyledTextMaxLine>{'Section Items'}</StyledTextMaxLine>
      {sections.map((section, index) => (
        <SectionSummery
          key={section?.uuid || `section-${index}`}
          section={section}
          sectionIndex={index}
          expanded={expanded}
          setExpanded={setExpanded}
          status={sectionStatus[index]}
          saving={saving}
          readOnly={readOnly}
          locked={locked}
          onEditSection={() => openEditSection(index)}
          onDeleteSection={() => setDeleteSectionIndex(index)}
          onNewLesson={() => openNewLesson(index)}
          onEditLesson={(uuid) => openEditLesson(index, uuid)}
          onDeleteLesson={(uuid) => onDeleteItem(index, uuid)}
          onMoveLesson={(uuid, delta) => moveLesson(index, uuid, delta)}
          onClearError={() => clearSectionError(index)}
        />
      ))}
      {sections.length === 0 && (
        <Stack
          sx={{
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <TableNoData isNotFound={true} />
        </Stack>
      )}

      <SectionModal
        onSubmit={onSubmitNewSection}
        currentSection={currentSection}
        handleClose={closeSectionModal}
        isEdit={editingSection !== null}
        open={sectionModalOpen}
      />
      <SectionItemModal
        currentItem={currentItem?.item}
        handleClose={closeLessonModal}
        isEdit={currentItem ? true : false}
        onSubmit={onSubmitNewSectionItem}
        modalStep={modalStep}
        setModalStep={setModalStep}
        open={modalOpen}
      />
      <ConfirmationDialog
        isOpen={deleteSectionIndex !== null}
        onClose={() => setDeleteSectionIndex(null)}
        confirmAction={async () => {
          if (deleteSectionIndex === null) return;
          const ok = await onDeleteSection(deleteSectionIndex);
          // The sections after it moved up one place.
          if (ok) setExpanded(false);
        }}
        loading={saving}
      />
    </Stack>
  );
}
